// Sugalarm — reading-detail popover (cycle 48).
//
// Opened by tapping a reading dot on the Now-screen's expanded chart
// (mobile + web). Read-only: shows the dot's timestamp, glucose value
// + zone, the trend arrow at that moment, and the insulin-on-board at
// that moment (the same dual-ring IOB math the rest of the app uses,
// evaluated at the reading's timestamp instead of `now`).
//
// Mirrors the cycle-43 ActiveBolusesModal / cycle-44 HistoryLegend
// pattern: no portal, rendered in the React tree, a transparent
// fixed backdrop that closes on outside tap. It deliberately slots
// one z-index below ActiveBolusesModal (backdrop 354 / card 355 vs.
// the IOB editor's 356) because the IOB editor is a real editing
// flow and this is a read-only peek.

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  displayValue,
  fmtTime,
  trendInfo,
  zoneColor,
  zoneLabel,
  zoneOf,
} from "../../lib/glucose";
import { activeBolusesForRings, totalIOB } from "../../lib/iob";
import type { Reading, Thresholds, Treatment } from "../../lib/types";
import { TrendArrow } from "./TrendArrow";
import styles from "./ReadingDetailPopover.module.css";

interface Props {
  reading: Reading;
  treatments: Treatment[];
  thresholds: Thresholds;
  units: "mg/dL" | "mmol/L";
  h24: boolean;
  /** Screen rect of the tapped dot (viewport coords). */
  anchorRect: DOMRect | null;
  onClose: () => void;
}

const GAP = 8;
const MARGIN = 8;

function fmtDate(ts: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(ts));
}

export function ReadingDetailPopover({
  reading,
  treatments,
  thresholds,
  units,
  h24,
  anchorRect,
  onClose,
}: Props) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Until measured, render off-screen so a first-paint flash doesn't
  // land in the wrong place; useLayoutEffect places it before paint.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || !anchorRect) return;
    const { width: pw, height: ph } = card.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Centre horizontally over the dot, clamped to the viewport so it
    // never bleeds off a phone edge.
    let left = anchorRect.left + anchorRect.width / 2 - pw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - pw - MARGIN));

    // Prefer above the dot; flip below when there isn't room (the dot
    // is near the top of the chart / viewport).
    let top = anchorRect.top - ph - GAP;
    if (top < MARGIN) top = anchorRect.bottom + GAP;
    // Last-resort clamp so a very tall card stays on screen.
    top = Math.max(MARGIN, Math.min(top, vh - ph - MARGIN));

    setPos({ top, left });
  }, [anchorRect, reading.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const zone = zoneOf(reading.sgv, thresholds);
  const trend = trendInfo(reading.trend, reading.trend_rate);
  const momentMs = new Date(reading.timestamp).getTime();
  // IOB *at the reading's moment*: only boluses given at or before
  // this reading count (a later bolus has zero bearing on the past).
  // Pre-filtering keeps us on the existing lib/iob.ts math
  // (activeBolusesForRings + totalIOB, evaluated at `momentMs`)
  // without modifying that module — `activeBolusesForRings` alone
  // would treat a future bolus as full IOB (negative elapsed → 1.0).
  const priorTreatments = treatments.filter(
    (t) => new Date(t.timestamp).getTime() <= momentMs,
  );
  const iob = totalIOB(
    activeBolusesForRings(priorTreatments, momentMs),
    momentMs,
  );

  return (
    <>
      {/* Transparent backdrop — one level below ActiveBolusesModal
          (356). Closes on outside tap. */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 354 }}
        onClick={onClose}
      />
      <div
        ref={cardRef}
        className={styles.card}
        style={{
          position: "fixed",
          zIndex: 355,
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          visibility: pos ? "visible" : "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Reading detail"
      >
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <div className={styles.when}>
          {fmtDate(reading.timestamp)} · {fmtTime(reading.timestamp, { h24 })}
        </div>

        <div className={styles.valueRow}>
          <span className={`${styles.value} tnum`}>
            {displayValue(reading.sgv, units)}
          </span>
          <span className={styles.units}>{units}</span>
          <span className={styles.zone}>
            <span
              className={styles.zoneDot}
              style={{ background: zoneColor(zone) }}
            />
            {zoneLabel(zone)}
          </span>
          <span className={styles.trend}>
            <TrendArrow glyph={trend.glyph} zone={zone} size={28} />
          </span>
        </div>

        <div className={styles.iob}>
          IOB at this moment: <span className="tnum">{iob.toFixed(1)} u</span>
        </div>
      </div>
    </>
  );
}
